import React, {FC} from "react"
import {AsyncResultComponent} from "../../containers/AsyncResult/AsyncResult"
import {
	CheckDeposit,
	CheckDepositStatus,
	CheckDepositVendors,
	CheckImageSide,
	getCheckDepositImage,
	getCheckDepositRequest,
} from "../../resources/checkDeposits"
import {useIsOrgAdmin, useIsUnitUser} from "../../services/auth"
import {useAsyncResult} from "../../hooks/useAsyncResult"
import {ErrorDocument, OkDocument, Resource} from "../../resources/common"
import moment from "moment"
import Icon from "../Icon/Icon"
import Skeleton from "react-loading-skeleton"
import {useModal} from "react-modal-hook"
import {RejectCheckModal} from "./RejectCheckModal"
import {ApproveCheckModal} from "./ApproveCheckModal"
import {CancelCheckModal} from "./CancelCheckModal"
import {ClearCheckModal} from "./ClearCheckModal"
import {ReturnCheckModal} from "./ReturnCheckModal"
import {kebabCase, startCase} from "lodash"
import classNames from "classnames"
import {DrawerHeader} from "../Drawer/Drawer"
import {KeyValueCard, KeyValueCardContainer, KeyValueCardKey, KeyValueCardValue} from "../KeyValueCard/KeyValueCard"
import {AsyncResultRequestState} from "../../types/asyncResult"
import {mapUsersIdToName} from "../../resources/user"
import {NamesAtUnitCard} from "../NamesAtUnit/NamesAtUnit"
import {isDepositOrFBOAccount} from "../../resources/account"
import {AccountCard} from "../Accounts/AccountCard"
import {CustomerCard} from "../Customers/CustomerCard"
import {Transactions} from "../Transactions/Transactions"
import {TransactionsColumns} from "../Transactions/Transactions.types"
import {CheckDeposits} from "./CheckDeposits"
import {UnitUserOnly} from "../../containers/PermissionedUser/PermissionedUser"
import {CheckImageCard} from "./CheckImageCard"
import {CheckDepositDetails} from "./CheckDepositDetails"
import {CheckDepositColumns} from "./CheckDeposit.types"
import {ActionButtons, getPermittedButtons, hasAnyCheckDepositPermission} from "./checkDepositPermissions"
import {getButtonsToShow, getCheckActionsTitle, waitingForOcrLabel} from "./checkDepositActions"
export {disabledActionsEnsentaTooltipValue} from "./checkDepositActions"

interface SingleCheckDepositProps {
	id: string
	token: string
	refresh: () => void
	useDrawer: boolean
	checkDepositAsyncResult?: AsyncResultRequestState<OkDocument<CheckDeposit>, ErrorDocument>
}

interface DecisionDetailsProps {
	decision: "Approved" | "Rejected"
	reason?: string
	reasonText?: string
	time?: Date
	user?: string
}

const internalDecisionReadyStatuses: Array<keyof typeof CheckDepositStatus> = [
	CheckDepositStatus.Pending,
	CheckDepositStatus.Rejected,
	CheckDepositStatus.Clearing,
	CheckDepositStatus.Sent,
	CheckDepositStatus.Canceled,
	CheckDepositStatus.Returned,
]

const DecisionDetails: FC<DecisionDetailsProps> = ({decision, reason, time, user, reasonText}) => {
	return (
		<KeyValueCard title={"Decision Details"}>
			<KeyValueCardContainer>
				{decision && (
					<>
						<KeyValueCardKey> Decision</KeyValueCardKey>
						<KeyValueCardValue> {decision} </KeyValueCardValue>
					</>
				)}

				{reason && (
					<>
						<KeyValueCardKey> Reason</KeyValueCardKey>
						<KeyValueCardValue> {reasonText ? `${reason} (${reasonText})` : reason} </KeyValueCardValue>
					</>
				)}

				{time && (
					<>
						<KeyValueCardKey> Decision Date</KeyValueCardKey>
						<KeyValueCardValue> {moment(time).format("MM/DD/YYYY h:mm A")} </KeyValueCardValue>
					</>
				)}

				{user && (
					<>
						<KeyValueCardKey> Decisioned By</KeyValueCardKey>
						<KeyValueCardValue> {user} </KeyValueCardValue>
					</>
				)}
			</KeyValueCardContainer>
		</KeyValueCard>
	)
}

export function CheckActionButtons({
	id,
	refresh,
	buttons,
	disabled,
	title,
	vendor,
	small = false,
}: {
	id: string
	refresh: () => void
	buttons: ActionButtons
	disabled: boolean
	title: string
	vendor?: CheckDepositVendors
	small?: boolean
}) {
	if (!buttons) return null

	const [showRejectCheck, hideRejectCheck] = useModal(() => (
		<RejectCheckModal
			closeModal={hideRejectCheck}
			id={id}
			onSuccess={refresh}
			isVeryfi={vendor == CheckDepositVendors.Veryfi}
		/>
	))

	const [showApproveCheck, hideApproveCheck] = useModal(() => (
		<ApproveCheckModal closeModal={hideApproveCheck} id={id} onSuccess={refresh} />
	))

	const [showReturnCheck, hideReturnCheck] = useModal(() => (
		<ReturnCheckModal closeModal={hideReturnCheck} id={id} onSuccess={refresh} />
	))

	const [showCancelCheck, hideCancelCheck] = useModal(() => (
		<CancelCheckModal closeModal={hideCancelCheck} id={id} onSuccess={refresh} />
	))

	const [showClearCheck, hideClearCheck] = useModal(() => (
		<ClearCheckModal closeModal={hideClearCheck} id={id} onSuccess={refresh} />
	))

	return (
		<>
			{buttons.includes("Reject") && (
				<button
					className={classNames("button is-danger", small && "is-small")}
					onClick={() => showRejectCheck()}
					disabled={disabled}
					title={title}
				>
					<span className="icon">
						<Icon icon="interface-delete-interface-essential" size={12} />
					</span>
					<span> Reject </span>
				</button>
			)}

			{buttons.includes("Approve") && (
				<button
					className={classNames("button is-approved", small && "is-small")}
					onClick={() => showApproveCheck()}
					disabled={disabled}
					title={title}
				>
					<span className="icon">
						<Icon icon="interface-validation-check--interface-essential" size={12} />
					</span>
					<span> Approve </span>
				</button>
			)}

			{buttons.includes("Return") && (
				<button
					className={classNames("button is-danger", small && "is-small")}
					onClick={() => showReturnCheck()}
					disabled={disabled}
					title={title}
				>
					<span className="icon">
						<Icon icon="interface-validation-check--interface-essential" size={12} />
					</span>
					<span> Mark as returned </span>
				</button>
			)}

			{buttons.includes("Cancel") && (
				<button
					className={classNames("button is-danger", small && "is-small")}
					onClick={() => showCancelCheck()}
					disabled={disabled}
					title={title}
				>
					<span className="icon">
						<Icon icon="interface-delete-interface-essential" size={12} />
					</span>
					<span> Cancel </span>
				</button>
			)}

			{buttons.includes("Clear") && (
				<button
					className={classNames("button is-success", small && "is-small")}
					onClick={() => showClearCheck()}
					disabled={disabled}
					title={title}
				>
					<span className="icon">
						<Icon icon="interface-validation-check--interface-essential" size={12} />
					</span>
					<span> Clear Check </span>
				</button>
			)}
		</>
	)
}

function CheckDepositInfoPending() {
	return (
		<div className="check-content">
			<div className="check-image-card">
				<div className="check-image-card-container">
					<div className={"skeleton-placeholder"}>
						<Skeleton />
					</div>
				</div>
			</div>

			<KeyValueCard>
				<KeyValueCardContainer>
					<>
						<KeyValueCardKey>
							{" "}
							<Skeleton />{" "}
						</KeyValueCardKey>
						<KeyValueCardValue>
							{" "}
							<Skeleton />{" "}
						</KeyValueCardValue>
					</>
					<>
						<KeyValueCardKey>
							{" "}
							<Skeleton />{" "}
						</KeyValueCardKey>
						<KeyValueCardValue>
							{" "}
							<Skeleton />{" "}
						</KeyValueCardValue>
					</>
					<>
						<KeyValueCardKey>
							{" "}
							<Skeleton />{" "}
						</KeyValueCardKey>
						<KeyValueCardValue>
							{" "}
							<Skeleton />{" "}
						</KeyValueCardValue>
					</>
					<>
						<KeyValueCardKey>
							{" "}
							<Skeleton />{" "}
						</KeyValueCardKey>
						<KeyValueCardValue>
							{" "}
							<Skeleton />{" "}
						</KeyValueCardValue>
					</>
					<>
						<KeyValueCardKey>
							{" "}
							<Skeleton />{" "}
						</KeyValueCardKey>
						<KeyValueCardValue>
							{" "}
							<Skeleton />{" "}
						</KeyValueCardValue>
					</>
				</KeyValueCardContainer>
			</KeyValueCard>

			<KeyValueCard>
				<KeyValueCardContainer>
					<>
						<KeyValueCardKey>
							{" "}
							<Skeleton />{" "}
						</KeyValueCardKey>
						<KeyValueCardValue>
							{" "}
							<Skeleton />{" "}
						</KeyValueCardValue>
					</>
					<>
						<KeyValueCardKey>
							{" "}
							<Skeleton />{" "}
						</KeyValueCardKey>
						<KeyValueCardValue>
							{" "}
							<Skeleton />{" "}
						</KeyValueCardValue>
					</>
					<>
						<KeyValueCardKey>
							{" "}
							<Skeleton />{" "}
						</KeyValueCardKey>
						<KeyValueCardValue>
							{" "}
							<Skeleton />{" "}
						</KeyValueCardValue>
					</>
					<>
						<KeyValueCardKey>
							{" "}
							<Skeleton />{" "}
						</KeyValueCardKey>
						<KeyValueCardValue>
							{" "}
							<Skeleton />{" "}
						</KeyValueCardValue>
					</>
					<>
						<KeyValueCardKey>
							{" "}
							<Skeleton />{" "}
						</KeyValueCardKey>
						<KeyValueCardValue>
							{" "}
							<Skeleton />{" "}
						</KeyValueCardValue>
					</>
				</KeyValueCardContainer>
			</KeyValueCard>
		</div>
	)
}

interface CheckDepositContentProps {
	checkDeposit: {data: CheckDeposit; included: Resource[]}
	id: string
	accessToken: string
	refresh: () => void
	useDrawer: boolean
	frontImage?: ArrayBuffer | null
	backImage?: ArrayBuffer | null
	frontTiffImage?: ArrayBuffer | null
	backTiffImage?: ArrayBuffer | null
	expectsTiff?: boolean
}

const CheckDepositContentVerifyVendor: FC<CheckDepositContentProps> = (props: CheckDepositContentProps) => {
	const frontTiffImageState = useAsyncResult(() =>
		getCheckDepositImage({accessToken: props.accessToken, id: props.id, side: CheckImageSide.FrontTiff})
	)
	const backTiffImageState = useAsyncResult(() =>
		getCheckDepositImage({accessToken: props.accessToken, id: props.id, side: CheckImageSide.BackTiff})
	)

	const frontTiffImage = frontTiffImageState.match(
		() => undefined,
		(image) => image,
		() => null
	)
	const backTiffImage = backTiffImageState.match(
		() => undefined,
		(image) => image,
		() => null
	)

	return (
		<CheckDepositContent {...props} frontTiffImage={frontTiffImage} backTiffImage={backTiffImage} expectsTiff={true} />
	)
}

const CheckDepositContent: FC<CheckDepositContentProps> = ({
	checkDeposit,
	id,
	accessToken,
	refresh,
	useDrawer,
	frontImage,
	backImage,
	frontTiffImage,
	backTiffImage,
	expectsTiff,
}) => {
	const isOrgAdmin = useIsOrgAdmin()
	const isUnitUser = useIsUnitUser()
	const vendor = checkDeposit.data.attributes.vendor

	const checkId = checkDeposit.data.id
	const attributes = checkDeposit.data.attributes
	const waitingForOcr = attributes?.waitingForOcr === true
	const relationships = checkDeposit.data.relationships
	const included = checkDeposit.included
	const reason = attributes?.reason
	const reasonText = attributes?.reasonText
	const status = attributes?.status
	const updatedById = attributes?.statusSetBy
	const updatedAt = attributes?.statusCreatedAt
	const tags = attributes?.tags
	const checkActionsTitle = getCheckActionsTitle({waitingForOcr, vendor, isOrgAdmin, isUnitUser})
	const userIdToName = mapUsersIdToName(included)
	const updateByName = updatedById ? userIdToName.get(String(updatedById)) : undefined
	const depositAccount = included?.find((v) => v.type == "depositAccount")

	return (
		<>
			{useDrawer && (
				<DrawerHeader title={`Check #${id}`}>
					<div className={classNames("check-deposit-status", kebabCase(status))}>{startCase(status)}</div>
				</DrawerHeader>
			)}
			{waitingForOcr && (
				<div className="check-deposit-waiting-for-ocr notification is-warning" role="status">
					{waitingForOcrLabel}
				</div>
			)}
			<div className="check-content">
				{useDrawer && (
					<CheckImageCard
						frontImage={frontImage}
						backImage={backImage}
						frontTiffImage={frontTiffImage}
						backTiffImage={backTiffImage}
						expectsTiff={expectsTiff}
						id={checkId}
						status={attributes.status}
					/>
				)}
				<CheckDepositDetails
					title="Check Details"
					amount={attributes.amount}
					number={attributes.checkNumber}
					customerId={relationships.customer?.data.id}
					customerIds={relationships.customers?.data.map((c) => c.id)}
					accountId={relationships.account?.data.id}
					depositDate={attributes.createdAt}
					description={attributes.description}
					include={included}
					settlementDate={attributes.settlementDate}
					tags={tags}
					orgId={useIsUnitUser() ? relationships.org?.data.id : undefined}
				/>
				{!useDrawer && (
					<>
						{relationships.customer?.data.id && (
							<UnitUserOnly>
								<NamesAtUnitCard customerId={`${relationships.customer?.data.id || ""}`} />
							</UnitUserOnly>
						)}
						{depositAccount && isDepositOrFBOAccount(depositAccount) && (
							<AccountCard account={depositAccount} accessToken={accessToken} />
						)}
						{relationships.customer?.data.id && <CustomerCard customerId={relationships.customer.data.id.toString()} />}
						{attributes.status === "PendingReview" && (
							<>
								<CheckDeposits
									title={"Recent Check Deposits"}
									fullHeight={false}
									stickyAction={false}
									enablePaging={false}
									enableStatusFilter={false}
									enableDateFilter={false}
									enableSearch={false}
									enableExport={false}
									enableRowClick={false}
									enableTitle
									disableDrawer
									limit={10}
									includedColumns={[
										CheckDepositColumns.id,
										CheckDepositColumns.status,
										CheckDepositColumns.reason,
										CheckDepositColumns.depositDate,
										CheckDepositColumns.amount,
									]}
									customerId={relationships.customer?.data.id.toString()}
								/>
								<Transactions
									enableAmountFilter={false}
									enableDateFilter={false}
									enableTypeFilter={false}
									enableOrgFilter={false}
									enableDirectionFilter={false}
									isUsingPaging={false}
									fullHeight={false}
									accountId={`${relationships.account?.data.id}`}
									limit={5}
									enableSearch={false}
									enableExport={false}
									title={"Recent Transactions"}
									token={accessToken}
									includedColumns={[
										TransactionsColumns.id,
										TransactionsColumns.type,
										TransactionsColumns.summary,
										TransactionsColumns.createdAt,
										TransactionsColumns.balance,
										TransactionsColumns.amount,
									]}
								/>
							</>
						)}
					</>
				)}
				{internalDecisionReadyStatuses.includes(status) && (
					<DecisionDetails
						time={updatedAt}
						user={updateByName}
						decision={status == CheckDepositStatus.Rejected ? CheckDepositStatus.Rejected : "Approved"}
						reason={reason}
						reasonText={reasonText}
					/>
				)}
			</div>
			{useDrawer && hasAnyCheckDepositPermission(getButtonsToShow(status, vendor, waitingForOcr), isOrgAdmin) && (
				<div className="drawer-action-buttons buttons is-left">
					<CheckActionButtons
						id={checkId}
						refresh={refresh}
						buttons={getPermittedButtons(getButtonsToShow(status, vendor, waitingForOcr), isOrgAdmin)}
						title={checkActionsTitle}
						disabled={waitingForOcr || (vendor === CheckDepositVendors.Ensenta && !isOrgAdmin && !isUnitUser)}
						vendor={vendor}
					/>
				</div>
			)}
		</>
	)
}

function CheckDepositInfoWithFetch({
	id,
	token,
	refresh,
	useDrawer,
}: Omit<SingleCheckDepositProps, "checkDepositAsyncResult">) {
	const checkDepositAsyncResult = useAsyncResult(() => getCheckDepositRequest({accessToken: token, id}), [token, id])

	return (
		<CheckDepositInfoContent
			id={id}
			accessToken={token}
			refresh={refresh}
			useDrawer={useDrawer}
			checkDepositAsyncResult={checkDepositAsyncResult}
		/>
	)
}

function CheckDepositInfoDrawer({
	id,
	accessToken,
	refresh,
	checkDepositAsyncResult,
}: {
	id: string
	accessToken: string
	refresh: () => void
	checkDepositAsyncResult: AsyncResultRequestState<OkDocument<CheckDeposit>, ErrorDocument>
}) {
	const frontImageState = useAsyncResult(
		() => getCheckDepositImage({accessToken, id, side: CheckImageSide.Front}),
		[accessToken, id]
	)
	const backImageState = useAsyncResult(
		() => getCheckDepositImage({accessToken, id, side: CheckImageSide.Back}),
		[accessToken, id]
	)

	const frontImage = frontImageState.match(
		() => undefined,
		(image) => image,
		() => null
	)
	const backImage = backImageState.match(
		() => undefined,
		(image) => image,
		() => null
	)

	return (
		<AsyncResultComponent asyncResult={checkDepositAsyncResult} pendingComponent={<CheckDepositInfoPending />}>
			{({value}) => {
				const checkDeposit = value as {data: CheckDeposit; included: Resource[]}
				const ContentComponent =
					checkDeposit.data.attributes.vendor === CheckDepositVendors.Veryfi
						? CheckDepositContentVerifyVendor
						: CheckDepositContent

				return (
					<ContentComponent
						checkDeposit={checkDeposit}
						id={id}
						accessToken={accessToken}
						refresh={refresh}
						useDrawer
						frontImage={frontImage}
						backImage={backImage}
					/>
				)
			}}
		</AsyncResultComponent>
	)
}

function CheckDepositInfoNoDrawer({
	id,
	accessToken,
	refresh,
	checkDepositAsyncResult,
}: {
	id: string
	accessToken: string
	refresh: () => void
	checkDepositAsyncResult: AsyncResultRequestState<OkDocument<CheckDeposit>, ErrorDocument>
}) {
	return (
		<AsyncResultComponent asyncResult={checkDepositAsyncResult} pendingComponent={<CheckDepositInfoPending />}>
			{({value}) => {
				const checkDeposit = value as {data: CheckDeposit; included: Resource[]}

				return (
					<CheckDepositContent
						checkDeposit={checkDeposit}
						id={id}
						accessToken={accessToken}
						refresh={refresh}
						useDrawer={false}
					/>
				)
			}}
		</AsyncResultComponent>
	)
}

function CheckDepositInfoContent({
	id,
	accessToken,
	refresh,
	useDrawer,
	checkDepositAsyncResult,
}: {
	id: string
	accessToken: string
	refresh: () => void
	useDrawer: boolean
	checkDepositAsyncResult: AsyncResultRequestState<OkDocument<CheckDeposit>, ErrorDocument>
}) {
	if (useDrawer) {
		return (
			<CheckDepositInfoDrawer
				id={id}
				accessToken={accessToken}
				refresh={refresh}
				checkDepositAsyncResult={checkDepositAsyncResult}
			/>
		)
	}

	return (
		<CheckDepositInfoNoDrawer
			id={id}
			accessToken={accessToken}
			refresh={refresh}
			checkDepositAsyncResult={checkDepositAsyncResult}
		/>
	)
}

export function CheckDepositInfo({id, token, refresh, useDrawer, checkDepositAsyncResult}: SingleCheckDepositProps) {
	if (checkDepositAsyncResult) {
		return (
			<CheckDepositInfoContent
				id={id}
				accessToken={token}
				refresh={refresh}
				useDrawer={useDrawer}
				checkDepositAsyncResult={checkDepositAsyncResult}
			/>
		)
	}

	return <CheckDepositInfoWithFetch id={id} token={token} refresh={refresh} useDrawer={useDrawer} />
}
